/**
 * Abspieler für die Arbeitsschritt-Animationen.
 *
 * Zeichnet die Vorlage mit requestAnimationFrame in ein SVG. Bei „Bewegung
 * reduzieren“ erscheint nur das Standbild mit Richtungspfeilen. Ist der
 * Abspieler nicht sichtbar oder die Seite im Hintergrund, steht er still.
 */
import { CANVAS, FONT_FAMILY, findTemplate, frameBody, renderBody, stillScene } from '@cookbook/animations';
import type { ParameterValue } from '@cookbook/animations';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useAnimationPalette, useIsVisible, usePrefersReducedMotion } from '../lib/animation.ts';
import { usePrinting } from '../lib/print.ts';
import { texts } from '../texts/de.ts';

/** Bilder je Sekunde; mehr braucht es für die ruhigen Bewegungen nicht. */
const FRAMES_PER_SECOND = 30;

/** Eigenschaften des Abspielers. */
export interface StepAnimationProps {
  readonly templateId: string | null;
  readonly params?: Readonly<Record<string, ParameterValue>>;
  readonly className?: string;
  /** Knopf zum Anhalten zeigen (Standard: ja). */
  readonly controls?: boolean;
}

/** Animation eines Arbeitsschritts. */
export function StepAnimation({
  templateId,
  params = {},
  className = '',
  controls = true,
}: StepAnimationProps) {
  const template = findTemplate(templateId);
  const palette = useAnimationPalette();
  // Beim Drucken genügt das Standbild.
  const printing = usePrinting();
  const reduced = usePrefersReducedMotion() || printing;
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const svgRef = useRef<SVGSVGElement | null>(null);
  const visible = useIsVisible(wrapperRef);
  const [paused, setPaused] = useState(false);
  // Abgespielte Zeit in Sekunden, bleibt beim Anhalten erhalten.
  const elapsed = useRef(0);
  const idPrefix = `anim${useId().replace(/[^a-zA-Z0-9]/g, '')}`;
  const paramsKey = JSON.stringify(params);
  const values = useMemo(() => JSON.parse(paramsKey) as Record<string, ParameterValue>, [paramsKey]);

  useEffect(() => {
    const svg = svgRef.current;
    if (template === undefined || svg === null) {
      return undefined;
    }
    const options = { palette, idPrefix, background: true };
    const draw = () => {
      svg.innerHTML = frameBody(template, values, (elapsed.current / template.durationSeconds) % 1, options);
    };
    if (reduced) {
      svg.innerHTML = renderBody(stillScene(template, values), options);
      return undefined;
    }
    draw();
    if (paused || !visible) {
      return undefined;
    }
    let frame = 0;
    let last = performance.now();
    let sinceDraw = 0;
    const tick = (now: number) => {
      const delta = Math.min(0.1, (now - last) / 1000);
      last = now;
      elapsed.current += delta;
      sinceDraw += delta;
      if (sinceDraw >= 1 / FRAMES_PER_SECOND) {
        sinceDraw = 0;
        draw();
      }
      frame = window.requestAnimationFrame(tick);
    };
    frame = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(frame);
  }, [template, values, palette, reduced, paused, visible, idPrefix]);

  if (template === undefined) {
    return null;
  }
  return (
    <div ref={wrapperRef} className={`relative overflow-hidden rounded-xl ${className}`}>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${CANVAS.width} ${CANVAS.height}`}
        role="img"
        aria-label={`${template.title}: ${template.description}`}
        strokeLinecap="round"
        strokeLinejoin="round"
        fontFamily={FONT_FAMILY}
        className="block h-auto w-full"
      />
      {controls && !reduced ? (
        <button
          type="button"
          onClick={() => setPaused((value) => !value)}
          aria-label={paused ? texts.animation.play : texts.animation.pause}
          aria-pressed={paused}
          className="absolute right-1.5 bottom-1.5 flex h-8 w-8 items-center justify-center rounded-full bg-surface-raised/90 text-xs text-content shadow"
        >
          {paused ? '▶' : '❚❚'}
        </button>
      ) : null}
    </div>
  );
}
